'use client';

/**
 * Insights card registry — `key` (from matrix) → React component.
 *
 * Single source of truth for "what does each card render?" Mirrors
 * the icon registry in CopilotPanel: matrix declares which cards
 * appear for each `(mode, selection)`; this map resolves each
 * `key` to the component that renders the card body. Adding a new
 * card means: one matrix entry on the backend (`INSIGHTS_CARDS` in
 * `total_matrix.py`) + one entry here.
 *
 * @see klyde_edge/ui_config/total_matrix.py — INSIGHTS_CARDS
 * @see klyde_edge/ui_config/docs/right_rail_and_canvas_ux_direction.md
 *
 * @layer presentation
 * @arch_component_type registry
 * @arch_domain copilot
 */

import { DetailsCard } from './details-card';
import { OpexAssumptionsCard } from '@/components/canvas/cards/finops/opex/opex-assumptions-card';
import { ObeyaSnapshotCard } from '@/components/canvas/cards/obeya/snapshot-card';
import { ObeyaHealthCard } from '@/components/canvas/cards/obeya/health-card';
import { ObeyaAttentionCard } from '@/components/canvas/cards/obeya/attention-card';
import { ObeyaQualityCard } from '@/components/canvas/cards/obeya/quality-card';
import { ObeyaProjectInfoCard } from '@/components/canvas/cards/obeya/project-info-card';
import { RfqHealthCard } from './rfq-health-card';
import { McpHealthCard } from './mcp-health-card';
import { McpWatchWindowCard } from './mcp-watch-window-card';
import { EntityDetailsCard } from './entity-details-card';
import { RelatedVendorDetailsCard } from './related-vendor-details-card';
import { IntentBriefCard } from './intent/brief-card';
import { IntentLinesCard } from './intent/lines-card';
import { IntentMatchQualityCard } from './intent/match-quality-card';
import { VendorBriefCard } from './vendors/brief-card';
import { VendorPerfCard } from './vendors/perf-card';
import { ProductPricePositionCard } from './products/price-position-card';
import { VendorActionsCard } from './vendors/actions-card';
import { SellerRfqsOpenCard } from './seller/rfqs-open-card';
import { SellerRecentActivityCard } from './seller/recent-activity-card';
import { ProductSearchInputCard } from '@/components/canvas/cards/products/search-input-card';
import { ProductFacetsCard } from '@/components/canvas/cards/products/facets-card';
// mode9 generic synopsis — borrowed verbatim from the canvas registry.
// Cards-everywhere: same component, both surfaces.
import { SysObeyaSynopsisCard } from '@/components/canvas/cards/system_obeya/synopsis-card';
// mode9 sel:coverage — the coverage narrator, rail-mounted.
import { AnalyticsCovSynopsisCard } from '@/components/canvas/cards/analytics/synopsis-card';
import { PlanPositionRoomDetailCard } from './plan-position-room-detail-card';

type InsightsCardComponent = React.ComponentType;

export const INSIGHTS_CARD_REGISTRY: Record<string, InsightsCardComponent> = {
  // Cards reused verbatim from the canvas registry — same primitive,
  // both surfaces. Each is a flex card with auto height; no
  // canvas-specific assumptions. Demonstrates the cards-everywhere
  // claim — surfaces differ by *layout discipline*, the card
  // primitive is the same.
  attention: ObeyaAttentionCard,
  snapshot: ObeyaSnapshotCard,
  details: DetailsCard,
  health: ObeyaHealthCard,
  quality: ObeyaQualityCard,
  // Composed Actor + ProjectConfig + ProjectProgramme read — client/
  // site/dates/budget/rooms in one card.
  project_info: ObeyaProjectInfoCard,
  // RFQ tender health — drill-down analog of `health`, mode1 sel:rfq.
  rfq_health: RfqHealthCard,
  mcp_details: () => <EntityDetailsCard strategyName="costplan_details" />,
  // Plan Position's rail detail — the room clicked on the canvas
  // rooms table (mode1_plan_position's own insights override).
  plan_position_room_detail: PlanPositionRoomDetailCard,
  // Plan Synthesis — cost-plan analog of `health`/`rfq_health`.
  mcp_health: McpHealthCard,
  // Always-visible plan/room totals — the Excel watch-window analogue.
  mcp_watch_window: McpWatchWindowCard,
  // The vendor riding alongside a product selection (1:1 via the
  // line) — self-suppresses when there's no related vendor to show.
  related_vendor_details: RelatedVendorDetailsCard,
  // Intent (mode3) rail cards — three-card split of the legacy
  // EntitySchema["intent"] paint. Brief + Match Quality share the
  // `intent_details` fetch via React Query dedup; Lines fires its
  // own `intent_lines` call.
  intent_brief: IntentBriefCard,
  intent_lines: IntentLinesCard,
  intent_match_quality: IntentMatchQualityCard,
  // Vendors (mode4) rail cards — selection-aware. Brief renders for
  // vendor / product / collection; perf is vendor-only; actions
  // renders for any of the three. Mode4-native — no imports of
  // legacy DetailsCard / VendorPerformance / Actions panels.
  vendor_brief: VendorBriefCard,
  vendor_perf: VendorPerfCard,
  product_price_position: ProductPricePositionCard,
  vendor_actions: VendorActionsCard,
  // product_search / product_facets — surface-agnostic, relocatable
  // cards (split from the old bundled rail card). Input flips the canvas
  // to search; facets refine. Both live under canvas/cards/products/ and
  // mount here on the rail today; the facets card relocates to the left
  // rail next via the matrix (placement change, no rewrite).
  product_search: ProductSearchInputCard,
  product_facets: ProductFacetsCard,
  // Mode3 picked-product rail. The underlying component is genuinely
  // type-agnostic (vendor / product / collection / entity_details
  // fallback) — the "Vendor" prefix is historical from when mode4
  // was the only consumer. Reusing it here flips mode3's product
  // selection from the legacy DetailsCard to the cards-everywhere
  // registry path. Component rename is a separate cleanup pass.
  intent_product_brief: VendorBriefCard,
  // Vendor portal (mode5) — seller landing rail stack. RFQs-open
  // surfaces the urgency story (deadline-sorted, overdue/today
  // counts); recent-activity shows submission status + a placeholder
  // for the buyer-message inbox (proper aggregation pending).
  seller_rfqs_open: SellerRfqsOpenCard,
  seller_recent_activity: SellerRecentActivityCard,
  // mode9 — generic LLM synopsis. Borrowed verbatim from the canvas
  // registry; rail mount is the live home, parallel-loads with the
  // canvas data. view_name comes from the active preset.
  sys_obeya_synopsis: SysObeyaSynopsisCard,
  analytics_cov_synopsis: AnalyticsCovSynopsisCard,
  // mode6 Opex — the assumptions behind the current selection (slice of the opex_today response).
  opex_assumptions: OpexAssumptionsCard,
  // Future cards drop in here as the matrix grows. Each card is a
  // stand-alone component; selection-awareness lives inside the card,
  // not in this registry.
};
